import React from 'react';
import { useEffect, useState } from 'react';
import { ToastContainer } from 'react-toastify';
import { useAccountStore } from '../../../components/Stores/accountStore';
import ButtonDesigned from '../../../components/UI-Elements/Buttons/ButtonDesigned';
import { ClientOrderQuote } from '../../../sp-types/clientOrderQuoteType';
import { submitQuote } from '../ClientQuotesUtils';

interface IHighlightData {
  title: string;
  value?: string | number;
  altText: string;
}

interface Props {
  clientOrder: ClientOrderQuote;
  currentPageIndex: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setIsLoadingExposure: (value: boolean) => void;
}

export const OrderSummary: React.FC<Props> = ({
  clientOrder,
  setCurrentPage,
  currentPageIndex,
  setIsLoadingExposure,
}) => {
  // eslint-disable-next-line
  const [isEditable, setIsEditable] = useState(true);
  const [additionalDetails, setAdditionalDetails] = useState<IHighlightData[]>([]);
  // eslint-disable-next-line
  const [isButtonLoading, setIsButtonLoading] = useState(false);
  const { account } = useAccountStore();

  useEffect(() => {
    const updateAdditionalDetails: IHighlightData[] = [];
    clientOrder.downPayments &&
      updateAdditionalDetails.push({ title: `Down payment ${clientOrder.downPayments}%`, altText: '' });
    clientOrder.changesFee &&
      updateAdditionalDetails.push({
        title: clientOrder.changesFee.label + clientOrder.changesFee.value + '%',
        altText: '',
      });
    clientOrder.cancellationFee &&
      updateAdditionalDetails.push({
        title: clientOrder.cancellationFee.label + clientOrder.cancellationFee.value + '%',
        altText: '',
      });

    setAdditionalDetails(updateAdditionalDetails);
  }, [clientOrder.downPayments, clientOrder.cancellationFee, clientOrder.changesFee]);

  function handleClear(): void {
    setCurrentPage(0);
  }

  const highlightData: IHighlightData[] = [
    {
      title: 'Product',
      altText: 'Choose product',
      value: clientOrder.productName,
    },
    {
      title: 'Product quantity',
      altText: 'Set quantity',
      value: clientOrder.quantity ? clientOrder.quantity.toLocaleString() : '',
    },
    {
      title: 'Price per unit',
      altText: 'Set price',
      value: clientOrder.pricePerUnit ? '$' + clientOrder.pricePerUnit.toLocaleString() : '',
    },
    {
      title: 'Total price',
      altText: 'Total price',
      value: clientOrder.totalExpectedPrice ? '$' + clientOrder.totalExpectedPrice?.toLocaleString() : '',
    },
    {
      title: 'Adjusted price per unit',
      altText: 'Future adjusted unit price',
      value: clientOrder.pricePerUnit ? '$' + (clientOrder.pricePerUnit - 28).toLocaleString() : '',
    },
    {
      title: 'Total adjusted price',
      altText: 'Future adjusted price',
      value: clientOrder.totalExpectedPrice ? '$' + (clientOrder.totalExpectedPrice! - 840_000).toLocaleString() : '',
    },

    {
      title: 'Material spot price',
      altText: 'Set material',
      value: clientOrder.materialName,
    },
    {
      title: 'Material future price ',
      altText: 'Set material',
      value: clientOrder.materialName,
    },
    {
      title: 'Material quantity',
      altText: 'Set quantity',
      value: clientOrder.materialQuantity ? clientOrder.materialQuantity?.toLocaleString() + 'Ton' : '',
    },
    {
      title: 'Formula',
      altText: 'Set formula',
      value: clientOrder.materialFormula,
    },
    // {
    //   title: 'Contract type',
    //   altText: 'Select contract type',
    //   value: clientOrder.contractType?.name,
    // },
    {
      title: 'Date range',
      altText: 'Set date range',
      value: clientOrder.DateRange,
    },
    {
      title: 'Total Material price',
      altText: 'Total price',
      value: clientOrder.totalExpectedPrice ? '$' + (30_750_000).toLocaleString() : '',
    },
  ];

  return (
    <div className="flex flex-col flex-1 h-full overflow-hidden py-[1rem]  xl:min-w-[20rem] lg:min-w-[13rem]   px-[1.25rem]  shadow-custom bg-white relative">
      <div className="text-black text-xl ">Order Summary</div>
      <div className="lg:pt-[1rem] xl:pt-[1rem] sm:pt-[0.5rem] md:pt-[0.5rem]  text-secondary_text text-sm ">
        Here are the highlights of your quote
      </div>
      <div className="flex flex-col justify-between overflow-auto  ">
        <div className="flex overflow-x-auto   sm:pt-[1rem] md:pt-[1rem] xl:space-y-[0.75rem] lg:space-y-[0.75rem] xl:flex-col lg:flex-col md:flex-row sm:flex-row xl:space-x-0 lg:space-x-0 md:space-x-[0.5rem] sm:space-x-[0.5rem]">
          {highlightData.map((item, index) => (
            <div
              key={item.title + index}
              className={`flex flex-col  pt-[0.25rem] pb-[0.5rem]  ${isEditable ? 'border px-[0.5rem] rounded' : ''}`}
            >
              <p className="text-sm mb-[0.5rem] text-gray-700 sm:hidden md:block lg:block xl:block">{item.title}</p>
              {item.value ? (
                <p className="text-base mb-[0] text-black ">{item.value}</p>
              ) : (
                <p className="text-base mb-[0] text-gray-400"> {item.altText}</p>
              )}
            </div>
          ))}
          {additionalDetails.length > 0 && (
            <div
              className={`flex flex-col space-y-[0.75rem] ${
                isEditable ? 'border px-[0.5rem] pt-[0.25rem] pb-[0.5rem] rounded' : ''
              }`}
            >
              <p className="text-sm mb-[0rem] text-gray-700 ">Additional details</p>
              {additionalDetails.map((item, index) => (
                <p key={item.title + index} className="text-base mb-[0] text-black ">
                  {item.title}
                </p>
              ))}
            </div>
          )}
        </div>
        <div className="flex lg:static xl:static md:absolute md:top-1 md:right-5 sm:absolute sm:top-1 sm:right-5 lg:justify-end xl:justify-end mt-[1rem] ">
          <div className="space-x-[1rem]">
            <ButtonDesigned style="outline" onButtonClick={handleClear}>
              Clear
            </ButtonDesigned>
            <ButtonDesigned
              style="primary"
              showToastInstedAction="You already have an active order"
              disableButton={
                !(
                  clientOrder.DueDate &&
                  clientOrder.productName &&
                  clientOrder.contractType &&
                  clientOrder.pricePerUnit
                ) ||
                isButtonLoading ||
                currentPageIndex === 5 ||
                currentPageIndex === 4
              }
              onButtonClick={
                currentPageIndex === 2
                  ? () =>
                      submitQuote(
                        account.id || '',
                        () => setCurrentPage(5),
                        (value: boolean) => setIsLoadingExposure(value),
                      )
                  : () => setCurrentPage(currentPageIndex + 1)
              }
              isFunctionLoading={isButtonLoading}
            >
              {currentPageIndex === 2 ? 'Submit' : 'Next'}
            </ButtonDesigned>
            <ToastContainer closeOnClick />
          </div>
        </div>
      </div>
    </div>
  );
};
